import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
    UserPlus,
    Copy,
    CheckCircle2,
    Clock,
    XCircle,
    Send,
    Sparkles,
    Trophy,
    Info,
} from "lucide-react";
import Layout from "../components/Layout";
import api, { formatApiError } from "../lib/api";

const STATUS_BADGE = {
    pending: { label: "En attente", cls: "bg-shariti-yellow/20 text-shariti-yellow", icon: Clock },
    approved: { label: "Validé", cls: "bg-emerald-500/20 text-emerald-400", icon: CheckCircle2 },
    rejected: { label: "Refusé", cls: "bg-red-500/20 text-red-400", icon: XCircle },
};

function formatDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString("fr-FR", {
            day: "numeric",
            month: "short",
            year: "numeric",
        });
    } catch (e) {
        return "—";
    }
}

export default function Cooptation() {
    const [data, setData] = useState(null);
    const [link, setLink] = useState(null);
    const [busy, setBusy] = useState(false);

    const reload = () => {
        api.get("/me/cooptations").then((r) => setData(r.data));
    };
    useEffect(() => {
        reload();
    }, []);

    const generateLink = async () => {
        setBusy(true);
        try {
            const { data: r } = await api.post("/me/cooptation/link");
            setLink(r);
            toast.success("Ton lien personnel est prêt — copie-le et envoie-le.");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Erreur");
        } finally {
            setBusy(false);
        }
    };

    const fullLink = link
        ? `${window.location.origin}/register?invite=${link.token}`
        : "";

    const copyLink = async () => {
        if (!fullLink) return;
        try {
            await navigator.clipboard.writeText(fullLink);
            toast.success("Lien copié");
        } catch (e) {
            const ta = document.createElement("textarea");
            ta.value = fullLink;
            document.body.appendChild(ta);
            ta.select();
            document.execCommand("copy");
            document.body.removeChild(ta);
            toast.success("Lien copié");
        }
    };

    const shareViaWhatsApp = () => {
        if (!fullLink) return;
        const text = `Je pense que tu serais top pour Shariti — viens voir : ${fullLink}`;
        window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
    };

    if (!data) {
        return (
            <Layout title="Cooptation" subtitle="Recommande quelqu'un de top">
                <div className="h-32 glass-card rounded-3xl animate-pulse" />
            </Layout>
        );
    }

    const cooptedAccepted = data.candidates.filter((c) => c.candidate_status === "approved").length;
    const cooptedPending = data.candidates.filter((c) => c.candidate_status === "pending").length;

    return (
        <Layout title="Cooptation" subtitle="Recommande quelqu'un qui partage tes valeurs">
            <div className="space-y-6" data-testid="cooptation-page">
                {/* Hero / KPI */}
                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="glass-card rounded-3xl p-5 lg:p-6 border border-shariti-orange/20 grid lg:grid-cols-[1.5fr_1fr] gap-6 items-start"
                >
                    <div>
                        <p className="text-[10px] font-thin tracking-[0.3em] uppercase text-shariti-orange-light mb-2">
                            Programme de cooptation
                        </p>
                        <h2 className="text-2xl lg:text-3xl font-extrabold text-white leading-tight">
                            Tu connais quelqu'un de bien ?
                            <br />
                            Recommande-le.
                        </h2>
                        <p className="text-sm text-white/60 font-light mt-3 max-w-xl leading-relaxed">
                            Génère un lien personnel, envoie-le à la personne. Si elle s'inscrit, tu
                            empoches{" "}
                            <span className="text-shariti-orange font-bold">
                                +{data.points_per_signup} pts
                            </span>{" "}
                            immédiatement. Si l'admin la valide ensuite comme recruteur,{" "}
                            <span className="text-shariti-orange font-bold">
                                +{data.points_per_approval} pts bonus
                            </span>
                            .
                        </p>
                        <div className="mt-4 flex items-start gap-2 text-xs text-white/50">
                            <Info className="w-3.5 h-3.5 mt-0.5 text-shariti-orange-light shrink-0" />
                            <span>
                                Plafond :{" "}
                                <strong className="text-white/80">
                                    {data.max_paid} cooptations rémunérées max
                                </strong>{" "}
                                — au-delà, le lien fonctionne mais ne rapporte plus.
                            </span>
                        </div>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                        <KPI label="Validées" value={cooptedAccepted} testid="cooptation-kpi-approved" />
                        <KPI label="En attente" value={cooptedPending} testid="cooptation-kpi-pending" />
                        <KPI
                            label="Slots restants"
                            value={data.remaining_paid_slots}
                            testid="cooptation-kpi-slots"
                            highlight
                        />
                    </div>
                </motion.div>

                {/* Link generator */}
                <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="glass-card rounded-3xl p-5 lg:p-6"
                >
                    <p className="text-xs font-thin tracking-[0.25em] uppercase text-white/50 mb-2">
                        Ton lien personnel
                    </p>
                    {!link ? (
                        <button
                            onClick={generateLink}
                            disabled={busy}
                            data-testid="cooptation-generate-link"
                            className="inline-flex items-center gap-2 bg-shariti-orange text-white font-extrabold py-3 px-5 rounded-2xl orange-glow hover:bg-shariti-orange-light transition-all disabled:opacity-60"
                        >
                            <Sparkles className="w-4 h-4" />
                            {busy ? "Génération..." : "Générer mon lien de cooptation"}
                        </button>
                    ) : (
                        <div className="space-y-3">
                            <div
                                className="flex flex-wrap items-center gap-3 p-4 rounded-2xl bg-shariti-orange/10 border border-shariti-orange/30"
                                data-testid="cooptation-link-card"
                            >
                                <UserPlus className="w-5 h-5 text-shariti-orange-light shrink-0" />
                                <p className="font-mono text-sm text-white truncate flex-1 min-w-0">
                                    {fullLink}
                                </p>
                                <button
                                    onClick={copyLink}
                                    data-testid="cooptation-copy-link"
                                    className="inline-flex items-center gap-1.5 bg-shariti-orange text-white font-extrabold text-xs uppercase tracking-widest px-3 py-2 rounded-xl hover:bg-shariti-orange-light transition-all"
                                >
                                    <Copy className="w-3.5 h-3.5" />
                                    Copier
                                </button>
                                <button
                                    onClick={shareViaWhatsApp}
                                    data-testid="cooptation-share-whatsapp"
                                    className="inline-flex items-center gap-1.5 bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-500/30 text-emerald-400 font-extrabold text-xs uppercase tracking-widest px-3 py-2 rounded-xl transition-all"
                                >
                                    <Send className="w-3.5 h-3.5" />
                                    WhatsApp
                                </button>
                            </div>
                            <p className="text-xs text-white/40">
                                Lien valable 30 jours, à usage unique. Une fois utilisé, regénère un
                                nouveau lien pour la prochaine personne.
                            </p>
                        </div>
                    )}
                </motion.div>

                {/* Candidates list */}
                <div>
                    <h3 className="text-lg font-extrabold text-white mb-3">
                        Mes cooptations ({data.candidates.length})
                    </h3>
                    {data.candidates.length === 0 ? (
                        <div
                            className="glass-card rounded-3xl p-8 text-center"
                            data-testid="cooptation-empty"
                        >
                            <UserPlus className="w-10 h-10 text-white/30 mx-auto mb-3" />
                            <p className="text-white/60">
                                Personne pour l'instant. Génère ton lien et partage-le !
                            </p>
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {data.candidates.map((c) => {
                                const badge = STATUS_BADGE[c.candidate_status] || STATUS_BADGE.pending;
                                const StatusIcon = badge.icon;
                                return (
                                    <div
                                        key={c.user_id}
                                        className="glass-card rounded-2xl p-4 flex items-center gap-4"
                                        data-testid={`cooptation-row-${c.user_id}`}
                                    >
                                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-shariti-orange to-shariti-yellow flex items-center justify-center font-bold text-shariti-black shrink-0">
                                            {c.name?.[0]}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="font-extrabold text-white truncate">
                                                {c.name}
                                            </p>
                                            <p className="text-xs text-white/50 truncate">
                                                {c.email} · inscrit le {formatDate(c.created_at)}
                                            </p>
                                            {c.rejection_reason && (
                                                <p className="text-xs text-red-400/80 italic mt-0.5">
                                                    Refus : {c.rejection_reason}
                                                </p>
                                            )}
                                        </div>
                                        <span
                                            className={`text-[10px] font-extrabold uppercase tracking-widest px-2 py-0.5 rounded-full ${badge.cls} inline-flex items-center gap-1`}
                                        >
                                            <StatusIcon className="w-3 h-3" />
                                            {badge.label}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </Layout>
    );
}

function KPI({ label, value, testid, highlight }) {
    return (
        <div
            className={`text-center p-4 rounded-2xl ${
                highlight ? "bg-shariti-orange/15 border border-shariti-orange/30" : "bg-white/5"
            }`}
            data-testid={testid}
        >
            <p className="text-[10px] font-thin tracking-widest uppercase text-white/50 mb-1">
                {label}
            </p>
            <p
                className={`text-2xl font-extrabold ${
                    highlight ? "text-shariti-orange" : "text-white"
                }`}
            >
                {value}
            </p>
        </div>
    );
}

// Add a Trophy import alias for tree-shaking
export { Trophy };
